/**
 * Дверь к просмотру картинок крупно (docs/homework-image-viewer-plan.md, Р1).
 *
 * ⚠️ **Единственное место, откуда грузится `ImageViewer.tsx`, и только через
 * `import()`.** За ним едет пакет `yet-another-react-lightbox` с плагинами и
 * своим CSS, а дверь статически импортируют форма выдачи (стартовый чанк),
 * материал ленты и фото решения (их ленивые чанки). Импорт значением
 * `ImageViewer.tsx` откуда угодно увёз бы пакет в чанк каждого, кто открыл
 * кабинет или домашку. Замки: четвёртая проверка `edu-answer-barrier` в
 * `apps/web/vite.config.ts` и `src/image-viewer-barrier.test.ts`.
 *
 * Сама дверь лёгкая: React, тексты и типы, без пакета.
 */
import {
  Component,
  lazy,
  Suspense,
  useState,
  type ComponentType,
  type LazyExoticComponent,
  type ReactNode,
} from "react";
import type { ViewerRequest } from "./viewer-model.ts";
import { viewerWordsOf, type ViewerWords } from "./viewer-text.ts";

type ViewerProps = { request: ViewerRequest; onClose: () => void };

/**
 * Ленивый компонент, заведённый на ОДНО обещание `import()`.
 *
 * ⚠️ `React.lazy` кэширует отклонённое обещание навсегда: после одного обрыва
 * сети просмотр не открылся бы до перезагрузки вкладки. Поэтому на отказ
 * ссылка сбрасывается, и следующее нажатие заводит новый `lazy()` на новое
 * обещание.
 */
let viewer: LazyExoticComponent<ComponentType<ViewerProps>> | null = null;

function lazyViewer(): LazyExoticComponent<ComponentType<ViewerProps>> {
  if (viewer !== null) return viewer;
  const made = lazy(() =>
    import("./ImageViewer.tsx").then(
      (m) => ({ default: m.ImageViewer }),
      (err: unknown) => {
        if (viewer === made) viewer = null;
        throw err;
      },
    ),
  );
  viewer = made;
  return made;
}

/**
 * Чанк не загрузился или слой упал: слой снимается, а под плитками остаётся
 * строка с причиной. Без границы отказ `import()` поднялся бы до корня и погасил
 * весь экран ради одной картинки. Про выкатку (сменились имена чанков) скажет
 * полоса «вышла новая версия» каркаса.
 */
class OpenBoundary extends Component<{ children: ReactNode; onFail: () => void }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(): void {
    this.props.onFail();
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Слой просмотра, пока `request` не `null`. Вызывающий ставит `request`
 * нажатием и снимает его в `onClose`.
 *
 * ⚠️ Пока едет чанк, фон НЕ `inert`: слой ожидания закрывает экран глазами, но
 * не клавиатурой (`inert` пакет ставит только своему порталу). Поэтому форма
 * выключает свои «убрать» и «Повторить», пока `request !== null` (Р1).
 */
export function ImageViewerDoor(props: { request: ViewerRequest | null; onClose: () => void }): ReactNode {
  const { request, onClose } = props;
  /** Слова последнего запроса, у которого открытие не удалось. */
  const [failed, setFailed] = useState<ViewerWords | null>(null);
  /** Счёт открытий: граница ошибки каждому новому открытию своя. */
  const [opens, setOpens] = useState(0);
  const [seen, setSeen] = useState<ViewerRequest | null>(request);

  // Новое нажатие снимает строку отказа и пробует снова
  if (request !== seen) {
    setSeen(request);
    if (request !== null) {
      setFailed(null);
      setOpens((n) => n + 1);
    }
  }

  const Viewer = request === null ? null : lazyViewer();
  // Слова по нажатому: «Открываем видео», а не «Открываем картинку»
  const words = request === null ? null : viewerWordsOf(request.noun, request.images[request.start]?.media?.kind);

  return (
    <>
      {request !== null && Viewer !== null && words !== null && (
        <OpenBoundary
          key={opens}
          onFail={() => {
            setFailed(words);
            onClose();
          }}
        >
          <Suspense
            fallback={
              <div className="imgview-wait">
                <p role="status">{words.opening}</p>
              </div>
            }
          >
            <Viewer request={request} onClose={onClose} />
          </Suspense>
        </OpenBoundary>
      )}
      {failed !== null && (
        <p className="err" role="status">
          {failed.openFailed}
        </p>
      )}
    </>
  );
}
